import React from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { AHText, Box, ListItem, TouchableBox } from "arkh";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { WAFileSettingsQuery } from "./__generated__/WAFileSettingsQuery.graphql";
import { ScrollView, View } from "react-native";
import { WAFileSettings_file$key } from "./__generated__/WAFileSettings_file.graphql";

import { useListContentStyle } from "../../../../../components/mobile/useListContentStyle";
import { ReorderableList } from "../../../../../components/mobile/ReoardrableList/ReordrableList";
import { FormattedMessage } from "react-intl";

type Props = {
  fileIds: string[];
  onRemoveFile: (id: string) => void;
  onAddFile: () => void;
  onSetOrder: (order: string[]) => void;
  slideshowDuration: number;
  setSlideShowDuration: (duration: number) => void;
};

export function WAFileSettings(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAFileSettings {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAFileSettings(
  props: Props & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WAFileSettingsQuery>(
    graphql`
      query WAFileSettingsQuery($fileIds: [ID!]!) {
        listFiles(fileIds: $fileIds) {
          id
          ...WAFileSettings_file
        }
      }
    `,
    {
      fileIds: props.fileIds.slice().sort(),
    },
    {
      fetchKey: props.fetchKey,
    }
  );

  const contentContainerStyle = useListContentStyle();

  const sorted = props.fileIds.flatMap((id) => {
    const found = data.listFiles.find((f) => f.id === id);
    return found ? [found] : [];
  });
  const renderItem = (item: (typeof sorted)[number]): React.JSX.Element => {
    return <FileListItem file={item} onRemove={props.onRemoveFile} />;
  };

  return (
    <View style={{ flex: 1 }}>
      <DurationSelector
        duration={props.slideshowDuration}
        setDuration={props.setSlideShowDuration}
      />
      <ScrollView style={contentContainerStyle}>
        <ReorderableList
          items={sorted}
          renderItem={renderItem}
          itemHeight={FILE_LIST_ITEM_HEIGHT}
          onReorder={(order) => {
            props.onSetOrder(
              Object.keys(order).sort((a, b) => order[a] - order[b])
            );
          }}
          keyExtractor={(item) => item.id}
          hasFooter
        />
        <ListItem
          isLast
          isFirst={data.listFiles.length === 0}
          onPress={props.onAddFile}
          right={<ListItem.AddButton />}
        >
          <ListItem.AddTitle>
            {/* Lägg till fil */}
            <FormattedMessage
              defaultMessage="Add file"
              description="WAFileSettings add file button"
            />
          </ListItem.AddTitle>
        </ListItem>
      </ScrollView>
    </View>
  );
}

function DurationSelector(props: {
  duration: number;
  setDuration: (duration: number) => void;
}) {
  const durations = [15, 30, 60];
  const d = durations.includes(props.duration) ? props.duration : durations[0];
  return (
    <Box spaceTop="large" spaceBottom="medium">
      <AHText spaceHorizontal="medium" spaceBottom="small" type="title-small">
        {/* Bildspelshastighet */}
        <FormattedMessage
          defaultMessage="Slideshow speed"
          description="WAFileSettings slideshow speed"
        />
      </AHText>
      <Box spaceHorizontal="medium" style={{ flexDirection: "row" }}>
        {durations.map((duration) => (
          <DurationButtion
            key={duration}
            duration={duration}
            selected={duration === d}
            onPress={() => {
              props.setDuration(duration);
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
function DurationButtion(props: {
  duration: number;
  selected?: boolean;
  onPress?: () => void;
}) {
  return (
    <Box spaceRight="small">
      <TouchableBox
        onPress={props.onPress}
        cornerRadius="medium"
        spaceHorizontal="medium"
        spaceVertical="small"
        color={props.selected ? "primary" : "surface"}
      >
        <AHText
          color={props.selected ? "lightText" : "darkerText"}
          type="subtitle"
        >
          {/* TODO: intl */}
          {props.duration}s
        </AHText>
      </TouchableBox>
    </Box>
  );
}

const FILE_LIST_ITEM_HEIGHT = 50;

function FileListItem(props: {
  file: WAFileSettings_file$key;

  onRemove: (id: string) => void;
}) {
  const file = useFragment(
    graphql`
      fragment WAFileSettings_file on File {
        id
        name
        ...FileIcon_File
      }
    `,
    props.file
  );

  return (
    <ListItem
      forceHeight={FILE_LIST_ITEM_HEIGHT}
      isReorderable
      left={<ListItem.RemoveButton onPress={() => props.onRemove(file.id)} />}
    >
      <ListItem.Title numberOfLines={1}>{file.name}</ListItem.Title>
    </ListItem>
  );
}
